import {DataTable} from '@management-ui/core';
import {Info} from '@mui/icons-material';
import {IconButton, styled, Tooltip, Typography} from '@mui/material';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import ConfirmedDate from '../../../../components/ConfirmedDate';
import OrderStatus from '../../../../components/OrderStatus';
import Value from '../../../../components/Value';
import {firstLine} from '../../../../utilities';
import {MTableToolbar} from '@material-table/core';
import MarginSummary from './MarginSummary';

const Title = styled('div')(() => ({
  alignItems: 'center',
  display: 'flex',
}));

const Orders = ({title, tooltip, columnSet, service, filter, baseRoute, archiveRoute}) => {
  const history = useHistory();
  /** @type {({current: DataTable})} */
  const tableRef = useRef();
  const filterRef = useRef('');
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const serialised = JSON.stringify(filter);
    if (tableRef.current && filterRef.current !== '' && filterRef.current !== serialised) {
      tableRef.current.refresh();
    }
    filterRef.current = serialised;
  }, [filter]);

  return (
    <DataTable
      ref={tableRef}
      title={(
        <Title>
          <Typography variant="h6" mr={2}>{title}</Typography>
          <Tooltip title={tooltip}>
            <IconButton>
              <Info/>
            </IconButton>
          </Tooltip>
        </Title>
      )}
      tableProps={useMemo(() => columnSet === 'sales' ? {
        components: {
          Toolbar: (props) => (
            <>
              <MTableToolbar {...props}/>
              <MarginSummary orders={orders}/>
            </>
          )
        }
      } : {}, [columnSet, orders])}
      columns={useMemo(() => [
        ...(columnSet === 'purchase' ? [
          {title: 'PO Number', field: 'reference'},
          {
            title: 'Loading Date',
            field: 'collection_date',
            render: item => <ConfirmedDate date={item.collection_date} confirmed={item.collection_date_confirmed}/>
          },
          {title: 'Supplier', field: 'supplier', render: item => item.supplier?.name ?? '-'},
          {title: 'Depot', field: 'depot', render: item => firstLine(item.delivery_address)}
        ] : [
          {title: 'Order Number', field: 'reference'},
          {
            title: 'Loading Date',
            field: 'loading_date',
            render: item => <ConfirmedDate date={item.loading_date} confirmed={item.loading_date_confirmed}/>
          },
          {title: 'Customer', field: 'customer', render: item => item.customer?.name ?? '-'},
          {title: 'Depot', field: 'depot', render: item => firstLine(item.collection_address)}
        ]),
        {
          title: 'Value',
          field: 'value',
          render: item => <Value value={item.value} currency={item.currency} finalised={!!item.weighed_at}/>
        },
        ...(columnSet === 'sales' ? [
          {
            title: 'Margin',
            field: 'margin',
            render: item => item.purchase_order ? <Value value={item.margin} currency={item.currency}/> : '-'
          },
        ] : []),
        {
          title: 'Status',
          sorting: false,
          field: 'locked',
          render: item => <OrderStatus order={item}/>
        },
      ], [columnSet])}
      loadData={useCallback(query => new Promise((resolve, reject) => {
        service.getTableOrders(query, filter)
          .then(response => {
            setOrders(response.data);
            resolve({
              data: response.data, page: response.meta.current_page - 1, totalCount: response.meta.total
            });
          }).catch(() => {
            setOrders([]);
            reject();
        });
      }), [service, filter])}
      onRowClick={useCallback((contact) => {
        history.push(reverse(contact.archived ? archiveRoute : baseRoute, {id: contact.id}));
      }, [baseRoute, archiveRoute, history])}
    />
  );
};

export default Orders;
